import { useState } from "react";
import { money, ownerAccount, people, type Account, type Currency, type Owner } from "../../money/types";
import { Fact } from "../commerce/shared";
import { Button } from "../ui/button";
import { FormSheet } from "../ui/workflow";
import "./balance-overview.css";

export type BalanceAccount = Account & { available_minor?: number; reserved_minor?: number };
const owners: Owner[] = ["kolya", "vlad"];
const currencyAccounts = [
  { id: "kolya_wallet_usdt", name: "Кошелёк", currency: "USDT" },
  { id: "kolya_htx_usdt", name: "HTX", currency: "USDT" },
  { id: "vlad_alipay_cny", name: "Alipay", currency: "CNY" },
] as const;
const known = (value: unknown) => typeof value === "number" && Number.isSafeInteger(value) ? value : null;
const available = (account?: BalanceAccount) => known(account?.available_minor ?? account?.balance_minor);
const amountOnly = (value: unknown, currency: Currency) => money(known(value), currency).replace(/ (?:₽|USDT|CNY|USD)$/, "");

export function BalanceOverview({ accounts }: { accounts: BalanceAccount[] }) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const find = (id: string, currency: Currency) => accounts.find(account => account.id === id && account.currency === currency);
  const rub = owners.map(owner => ({ owner, id: ownerAccount(owner), account: find(ownerAccount(owner), "RUB") }));
  const currencies = currencyAccounts.map(slot => ({ ...slot, account: find(slot.id, slot.currency) }));
  const selected = [...rub, ...currencies].find(slot => slot.id === selectedId)?.account;
  return <div className="finance-balances">
    <section aria-labelledby="finance-rub-heading">
      <h2 className="finance-balance-caption" id="finance-rub-heading">Доступно в рублях</h2>
      <div className="finance-rub-grid">{rub.map(({ owner, id, account }) => {
        const value = available(account);
        return <button type="button" key={owner} className="finance-rub-card" data-long={value != null && Math.abs(value) >= 1_000_000} disabled={!account} aria-label={`${people[owner]}, доступно ${money(value)}. Подробнее`} onClick={() => setSelectedId(id)}>
          <span>{people[owner]}</span><strong>{money(value)}</strong>
        </button>;
      })}</div>
    </section>
    <section aria-labelledby="finance-currency-heading">
      <h2 className="finance-balance-caption" id="finance-currency-heading">Валюта</h2>
      <div className="finance-currency-wrap"><table className="finance-currency-table" aria-label="Валютные счета по владельцам">
        <colgroup><col className="finance-account-column"/><col/><col/></colgroup>
        <thead><tr><th scope="col">Счёт / всего</th><th scope="col">Коля</th><th scope="col">Влад</th></tr></thead>
        <tbody>{currencies.map(({ id, name, currency, account }) => <tr key={id}>
          <th scope="row"><button type="button" disabled={!account} onClick={() => setSelectedId(id)} aria-label={`${name}. Подробнее`}>
            <span>{name}</span><small>{available(account) == null ? `— ${currency}` : money(available(account), currency)}</small>
          </button></th>
          {owners.map(owner => <td key={owner}>{amountOnly(account?.owner_balances?.[owner], currency)}</td>)}
        </tr>)}</tbody>
      </table></div>
    </section>
    {selected && <FormSheet open title={selected.name} description={selected.currency === "RUB" ? "Остаток и резерв" : "Остатки и владельцы"} onOpenChange={open => { if (!open) setSelectedId(null); }} footer={<Button variant="outline" onClick={() => setSelectedId(null)}>Закрыть</Button>}>
      <div>
        <Fact label="Доступно">{money(available(selected), selected.currency)}</Fact>
        {selected.currency === "RUB" ? <><Fact label="Остаток в учёте">{money(known(selected.balance_minor), selected.currency)}</Fact><Fact label="Зарезервировано">{money(known(selected.reserved_minor), selected.currency)}</Fact></> : owners.map(owner => <Fact key={owner} label={people[owner]}>{money(known(selected.owner_balances?.[owner]), selected.currency)}</Fact>)}
      </div>
    </FormSheet>}
  </div>;
}
